import { ErrorMessage, Field, Form, Formik } from "formik";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, NavLink, useSearchParams } from "react-router-dom";
import * as Yup from "yup";
import { shallow } from "zustand/shallow";
import { AppSettings } from "./../../config/app-settings.js";
import { AuthContext } from "../../config/auth";
import { SettingsContext } from "../../config/settings";
import { useAuthStore } from "../../stores/auth";

const initialValues = {
  username: "",
  password: "",
  remember_me: false,
  otp: "",
};

const validationSchema = Yup.object().shape({
  username: Yup.string().required(),
  password: Yup.string().required(),
  otp: Yup.string().optional(),
});

function Login() {
  const { t } = useTranslation();
  const settings = useContext(AppSettings);
  const { whitelabel } = useContext(SettingsContext);
  const { setToken } = useContext(AuthContext);
  const [me, login] = useAuthStore((state) => [state.me, state.login], shallow);
  const [showPassword, setShowPassword] = useState(false);

  const [queryParams] = useSearchParams();
  const next = queryParams.get("next");

  useEffect(() => {
    settings.handleSetAppSidebarNone(true);
    settings.handleSetAppHeaderNone(true);
    settings.handleSetAppContentClass("p-0");

    return () => {
      settings.handleSetAppSidebarNone(false);
      settings.handleSetAppHeaderNone(false);
      settings.handleSetAppContentClass("");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (values, { setErrors }) => {
    const { data, error } = await login({
      username: values.username,
      password: values.password,
    });
    if (error) {
      setErrors({ password: error.detail });
    } else {
      setToken(data.access_token);
    }
  };

  if (me) {
    return <Navigate to={next ? next : "/"} />;
  }

  return (
    <div className="login login-v1">
      <div className="login-container">
        <div className="login-header">
          <div className="brand">
            <div className="d-flex align-items-center">
              <span className="logo"></span> <span>{whitelabel.name}</span>
            </div>
            <small>{t("app_name")}</small>
          </div>
        </div>
        <div className="login-body">
          <div className="login-content fs-13px">
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              validateOnChange={false}
              validateOnBlur={false}
              validateOnMount={false}
              onSubmit={handleSubmit}
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                handleSubmit,
                isSubmitting,
                /* and other goodies */
              }) => (
                <Form>
                  <div className="mb-4">
                    <Field
                      type="text"
                      name="username"
                      className="form-control fs-13px h-45px is-invalid"
                      placeholder={t("auth.username")}
                    />
                  </div>
                  <div className="mb-4">
                    <div className="input-group">
                      <Field
                        name="password"
                        type={showPassword ? "text" : "password"}
                        className="form-control fs-13px h-45px is-invalid"
                        placeholder={t("auth.password")}
                      />
                      <button
                        className="btn btn-danger"
                        type="button"
                        onClick={() => setShowPassword((prevState) => !prevState)}
                      >
                        <i className="fa fa-eye-slash" />
                      </button>
                      <ErrorMessage
                        name="password"
                        render={(error) => (
                          <div className="invalid-feedback">
                            <ul className="list-unstyled">
                              {Array.isArray(error) ? (
                                error.map((e, i) => <li key={i}>{t(e.t)}</li>)
                              ) : (
                                <li>{t(error)}</li>
                              )}
                            </ul>
                          </div>
                        )}
                      />
                    </div>
                  </div>
                  <div className="form-check mb-4">
                    <Field name="remember_me" className="form-check-input" type="checkbox" />
                    <label className="form-check-label" htmlFor="remember_me">
                      {t("auth.remember_me")}
                    </label>
                    <NavLink to="/auth/forgot" className="float-end text-danger text-decoration-none">
                      {t("auth.forgot_password")}
                    </NavLink>
                  </div>

                  <div className="login-buttons">
                    <button type="submit" className="btn h-45px btn-red d-block w-100 btn-lg">
                      {t("auth.sign_in")}
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
